// pages/BatchReports/BatchReportsConfigurationPage.js
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Box,
  Divider,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from "@material-ui/core";

import RefreshIcon from "@material-ui/icons/Refresh";

import PageTitle from "components/PageTitle/PageTitle";
import CustomizedSnackbars from "components/CustomizedSnackbars/CustomizedSnackbars";

import ReactJson from "react-json-view";

import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { okaidia } from "react-syntax-highlighter/dist/esm/styles/prism";

import useStyles from "./styles";
import { dbGet, dbPutWithHeaders } from "utils/DBFetchers";

const ENDPOINT = "cli/batch/report_manifest";
const ENTRY_MODEL_ENDPOINT = "cli/batch/report_manifest/entry_model";

function prettyJson(obj) {
  return JSON.stringify(obj, null, 2);
}

/**
 * Coerce string inputs based on a spec "type" ("int" | "bool" | "string")
 * Falls back to default when invalid (keeping same behavior as your current code).
 */
function coerceBySpec(value, spec) {
  if (!spec || typeof spec !== "object") return value;

  const type = spec.type; // "int" | "bool" | "string"
  const nullable = Boolean(spec.nullable);
  const def = spec.default;

  // Only coerce strings; keep other types as-is
  if (typeof value !== "string") {
    // Nullability gate
    if (value === null && !nullable) return def;
    return value;
  }

  const s = value.trim();

  // Allow setting null via empty string if nullable
  if (nullable && s === "") return null;

  if (type === "int") {
    if (s === "") return def;
    // only integer
    if (/^-?\d+$/.test(s)) {
      const n = Number(s);
      if (!Number.isNaN(n)) return n;
    }
    return def;
  }

  if (type === "bool") {
    if (s.toLowerCase() === "true") return true;
    if (s.toLowerCase() === "false") return false;
    if (s === "") return def;
    return def;
  }

  // string (default): keep as string
  return value;
}

function normalizeReportConfig(cfg, entrySpec) {
  if (!cfg || typeof cfg !== "object" || Array.isArray(cfg)) return cfg;

  const out = { ...cfg };

  if (!entrySpec || typeof entrySpec !== "object") return out;

  for (const [field, spec] of Object.entries(entrySpec)) {
    if (Object.prototype.hasOwnProperty.call(out, field)) {
      out[field] = coerceBySpec(out[field], spec);
    }
  }

  return out;
}

function normalizeManifest(manifest, entrySpec) {
  if (!manifest || typeof manifest !== "object" || Array.isArray(manifest)) return manifest;

  const out = { ...manifest };

  for (const [cmdName, cfg] of Object.entries(out)) {
    if (cfg && typeof cfg === "object" && !Array.isArray(cfg)) {
      out[cmdName] = normalizeReportConfig(cfg, entrySpec);
    }
  }

  return out;
}

function buildDefaultEntryFromSpec(entrySpec) {
  const out = {};
  if (!entrySpec || typeof entrySpec !== "object") return out;

  for (const [field, spec] of Object.entries(entrySpec)) {
    out[field] = spec?.default;
  }
  return out;
}

export default function BatchReportsConfigurationPage() {
  const classes = useStyles();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [etag, setEtag] = useState("");

  // Spec/model from backend
  const [entrySpec, setEntrySpec] = useState(null); // object { field: {type, nullable, default} }
  const [defaultEntry, setDefaultEntry] = useState(null); // object with defaults

  // Source of truth
  const [manifestObj, setManifestObj] = useState({});
  const [baselineObj, setBaselineObj] = useState({});

  // Texto (preview)
  const [manifestText, setManifestText] = useState("");

  // 0 = Editor, 1 = Código (preview)
  const [tab, setTab] = useState(0);

  const [toast, setToast] = useState({
    open: false,
    severity: "info",
    message: "",
  });

  function showToast(severity, message) {
    setToast({ open: true, severity, message });
  }

  function closeToast() {
    setToast((t) => ({ ...t, open: false }));
  }

  const isDirtyObj = useMemo(() => {
    return JSON.stringify(manifestObj) !== JSON.stringify(baselineObj);
  }, [manifestObj, baselineObj]);

  const canSave = !loading && !saving && Boolean(etag) && isDirtyObj;

  const loadEntryModel = useCallback(async () => {
    try {
      const data = await dbGet(ENTRY_MODEL_ENDPOINT);

      // Backward/forward compatible:
      // - If backend returns { entry_spec, entry_model } we use both.
      // - If backend returns only spec (your current setup), we build defaults from spec.
      const spec = data?.entry_spec || data?.entrySpec || data;

      if (!spec || typeof spec !== "object") {
        setEntrySpec(null);
        setDefaultEntry(null);
        return;
      }

      setEntrySpec(spec);

      const model = data?.entry_model || data?.entryModel || buildDefaultEntryFromSpec(spec);
      setDefaultEntry(model);
    } catch {
      // keep UI working with no spec; user can still edit existing manifest
      setEntrySpec(null);
      setDefaultEntry(null);
      showToast("warning", "No se pudo cargar el modelo default desde el backend.");
    }
  }, []);

  const loadManifest = useCallback(async () => {
    setLoading(true);
    try {
      // Load entry model/spec first so normalization works consistently
      await loadEntryModel();

      const data = await dbGet(ENDPOINT);
      const obj = data.report_manifest_json || {};

      const normalized = normalizeManifest(obj, entrySpec || null);
      const txt = prettyJson(normalized);

      setEtag(data.etag || "");
      setManifestObj(normalized);
      setBaselineObj(normalized);
      setManifestText(txt);
    } catch {
      showToast("error", "No se pudo cargar el report manifest.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadEntryModel]);

  useEffect(() => {
    loadManifest();
  }, [loadManifest]);

  async function onReload() {
    const ok = window.confirm("¿Recargar desde el servidor?\nSe perderán cambios no guardados.");
    if (!ok) return;

    await loadManifest();
    showToast("info", "reports_manifest.json recargado.");
  }

  async function onSave() {
    const ok = window.confirm("¿Confirmás guardar el reports_manifest.json?");
    if (!ok) return;

    setSaving(true);
    try {
      const payload = { report_manifest_json: manifestObj };

      const data = await dbPutWithHeaders(ENDPOINT, payload, {
        "If-Match": `"${etag}"`,
      });

      const savedObj = data.report_manifest_json || manifestObj;
      const normalized = normalizeManifest(savedObj, entrySpec || null);
      const savedText = prettyJson(normalized);

      setEtag(data.etag || etag);
      setManifestObj(normalized);
      setBaselineObj(normalized);
      setManifestText(savedText);

      showToast("success", "reports_manifest.json actualizado.");
    }

    catch (err) {
      const errorKey = err?.errors?.key?.[0];
      const backendMsg = err?.errors?.description?.[0]; // <-- acá está lo importante

      const msg =
        errorKey === "REPORT_MANIFEST_ETAG_MISMATCH"
          ? "El reports_manifest.json fue editado. Recargá y reintentá."
          : backendMsg || "No se pudo guardar el reports_manifest.json.";

      showToast("error", msg);
    } finally {
      setSaving(false);
    }
  }

  function setUpdatedManifest(updated) {
    const normalized = normalizeManifest(updated, entrySpec || null);
    setManifestObj(normalized);
    setManifestText(prettyJson(normalized));
  }

  function handleJsonEdit({ updated_src }) {
    setUpdatedManifest(updated_src);
    return true;
  }

  function handleJsonAdd({ updated_src, namespace }) {
    const isRootAdd = Array.isArray(namespace) && namespace.length === 0;

    if (isRootAdd) {
      const beforeKeys = new Set(Object.keys(manifestObj || {}));
      const afterKeys = Object.keys(updated_src || {});
      const addedKey = afterKeys.find((k) => !beforeKeys.has(k));

      if (addedKey) {
        // Use backend defaults when available; fallback to previous behavior if not.
        updated_src[addedKey] = {
          ...(defaultEntry || { description: "", cpus: 2, ram_gb: 4 }),
        };
      }

      // Clean accidental "null" key created by the editor/handler
      if (Object.prototype.hasOwnProperty.call(updated_src, "null")) {
        delete updated_src["null"];
      }
    }

    setUpdatedManifest(updated_src);
    return true;
  }

  return (
    <Grid>
      <PageTitle title="Tareas - Configuración" />

      <Paper elevation={3} className={classes.paperContainer}>
        <Box p={2}>
          <Grid container spacing={2}>
            {/* Header row: Tabs (left) + Actions (right) */}
            <Grid item xs={12}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                style={{ gap: 12 }}
              >
                <Tabs
                  value={tab}
                  onChange={(_e, v) => setTab(v)}
                  indicatorColor="primary"
                  textColor="primary"
                >
                  <Tab label="Editor" />
                  <Tab label="Código" />
                </Tabs>

                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <Tooltip title="Recargar">
                    <span>
                      <IconButton onClick={onReload} disabled={loading || saving}>
                        <RefreshIcon />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onSave}
                    disabled={!canSave}
                    startIcon={saving ? <CircularProgress size={18} /> : null}
                  >
                    Guardar
                  </Button>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              {loading ? (
                <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                  <CircularProgress size={22} />
                  <Typography>Cargando reports_manifest.json</Typography>
                </Box>
              ) : (
                <Box mt={1}>
                  {tab === 0 ? (
                    <div
                      style={{
                        border: "1px solid #ddd",
                        borderRadius: 8,
                        padding: 12,
                        background: "#fafafa",
                      }}
                    >
                      <ReactJson
                        src={manifestObj}
                        name={false}
                        collapsed={1}
                        enableClipboard={true}
                        displayDataTypes={false}
                        displayObjectSize={true}
                        onEdit={handleJsonEdit}
                        onAdd={handleJsonAdd}
                        onDelete={handleJsonEdit}
                        style={{ fontSize: 13 }}
                      />
                    </div>
                  ) : (
                    <>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        reports_manifest.json (solo lectura).
                      </Typography>

                      <div
                        style={{
                          borderRadius: 8,
                          overflow: "hidden",
                          border: "1px solid #ddd",
                        }}
                      >
                        <SyntaxHighlighter
                          language="json"
                          style={okaidia}
                          customStyle={{
                            margin: 0,
                            maxHeight: 520,
                            overflow: "auto",
                            fontSize: 13,
                          }}
                        >
                          {manifestText}
                        </SyntaxHighlighter>
                      </div>
                    </>
                  )}
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <CustomizedSnackbars
        open={toast.open}
        severity={toast.severity}
        message={toast.message}
        onClose={closeToast}
      />
    </Grid>
  );
}
